import { ICostAnalysisState } from '@/modules/costAnalysis/store/state';
import { MutationTree } from 'vuex';
import * as MT from './mutationsTypes';
import {
  AllAnalysisData,
  AnalysisPensionScheme,
  AnalysisFunds,
  FundAnalysisData,
  FundCost,
  AppliedAnalysisFilter,
  PaginationMeta,
  Paginated
} from '../types';
import Organisation from '../../../models/Organisation';
import OrganisationType from '../../../models/OrganisationType';
import TimePeriod from '../../../models/TimePeriod';
import { SubFund } from '../../subFund/types';
import { Fund } from '../../fund/types';
/*--------------------------------------------------/
 * Cost Analysis Mutations
 *--------------------------------------------------*/
export const CostAnalysisMutations: ICostAnalysisMutations = {
  [MT.SET_FETCHING_DATA]: (state: ICostAnalysisState, fetchingData: boolean) =>
    (state.fetchingData = fetchingData),

  [MT.SET_ANALYSIS_DATA]: (
    state: ICostAnalysisState,
    analysisData: AllAnalysisData
  ) => (state.analysisData = analysisData),

  [MT.SET_FUND_ANALYSIS_DATA]: (
    state: ICostAnalysisState,
    fundAnalysisData: FundAnalysisData
  ) => (state.fundAnalysisData = fundAnalysisData),

  [MT.SET_PAGINATED_FUND_ANALYSIS]: (
    state: ICostAnalysisState,
    paginatedFundAnalysis: FundCost[]
  ) => (state.paginatedFundAnalysis = paginatedFundAnalysis),

  [MT.SET_FUND_ANALYSIS_PAGINATION]: (
    state: ICostAnalysisState,
    analysisPaginationMeta: PaginationMeta
  ) => (state.analysisPaginationMeta = analysisPaginationMeta),

  [MT.SET_PAGINATED_FUNDS]: (
    state: ICostAnalysisState,
    paginatedFunds: Paginated<Fund>
  ) => (state.paginatedFunds = paginatedFunds),

  [MT.SET_ALL_ASSET_CLASSES]: (
    state: ICostAnalysisState,
    allAssetClasses: OrganisationType[]
  ) => (state.allAssetClasses = allAssetClasses),

  [MT.SET_ALL_TIME_PERIODS]: (
    state: ICostAnalysisState,
    allTimePeriods: TimePeriod[]
  ) => (state.allTimePeriods = allTimePeriods),

  [MT.SET_ALL_ORGANISATION_TYPES]: (
    state: ICostAnalysisState,
    allOrganisationTypes: OrganisationType[]
  ) => (state.allOrganisationTypes = allOrganisationTypes),

  [MT.SET_ALL_ASSET_MANAGERS]: (
    state: ICostAnalysisState,
    allAssetManagers: Organisation[]
  ) => (state.allAssetManagers = allAssetManagers),

  [MT.SET_ALL_FUNDS_PER_ORGANISATION]: (
    state: ICostAnalysisState,
    allFunds: AnalysisFunds[]
  ) => (state.allFunds = allFunds),

  [MT.SET_ALL_EXPOSED_FUNDS_PER_ORGANISATION]: (
    state: ICostAnalysisState,
    allExposedFunds: AnalysisFunds[]
  ) => (state.allExposedFunds = allExposedFunds),

  [MT.SET_ALL_EXPOSED_PENSION_SCHEMES]: (
    state: ICostAnalysisState,
    allExposedPensionSchemes: AnalysisPensionScheme[]
  ) => (state.allExposedPensionSchemes = allExposedPensionSchemes),

  [MT.SET_CURRENT_ORGANISATION]: (
    state: ICostAnalysisState,
    currentOrganisation: Organisation
  ) => (state.currentOrganisation = currentOrganisation),

  [MT.SET_APPLIED_FILTERS]: (
    state: ICostAnalysisState,
    appliedAllAnalysisFilter: AppliedAnalysisFilter
  ) => (state.appliedAllAnalysisFilter = appliedAllAnalysisFilter),

  [MT.SET_FILTER_FUNDS]: (
    state: ICostAnalysisState,
    filterFunds: AnalysisFunds[]
  ) => (state.filterFunds = filterFunds),

  [MT.SET_FILTER_PENSION_SCHEMES]: (
    state: ICostAnalysisState,
    filterPensionSchemes: AnalysisPensionScheme[]
  ) => (state.filterPensionSchemes = filterPensionSchemes),

  [MT.SET_FILTER_SUB_FUNDS]: (
    state: ICostAnalysisState,
    filterSubFunds: SubFund[]
  ) => (state.filterSubFunds = filterSubFunds),

  [MT.SET_FILTER_ASSET_CLASSES]: (
    state: ICostAnalysisState,
    filterAssetClasses: OrganisationType[]
  ) => (state.filterAssetClasses = filterAssetClasses),

  [MT.SET_FILTER_TIME_PERIODS]: (
    state: ICostAnalysisState,
    filterTimePeriods: TimePeriod[]
  ) => (state.filterTimePeriods = filterTimePeriods)
};

/*--------------------------------------------------/
 * Cost Analysis Mutations Tree Interface
 *--------------------------------------------------*/
export interface ICostAnalysisMutations
  extends MutationTree<ICostAnalysisState> {
  [MT.SET_FETCHING_DATA](
    state: ICostAnalysisState,
    fetchingData: boolean
  ): void;

  [MT.SET_ANALYSIS_DATA](
    state: ICostAnalysisState,
    analysisData: AllAnalysisData
  ): void;

  [MT.SET_FUND_ANALYSIS_DATA](
    state: ICostAnalysisState,
    fundAnalysisData: FundAnalysisData
  ): void;

  [MT.SET_PAGINATED_FUND_ANALYSIS](
    state: ICostAnalysisState,
    paginatedFundAnalysis: FundCost[]
  ): void;

  [MT.SET_FUND_ANALYSIS_PAGINATION](
    state: ICostAnalysisState,
    analysisPaginationMeta: PaginationMeta
  ): void;

  [MT.SET_PAGINATED_FUNDS](
    state: ICostAnalysisState,
    paginatedFunds: Paginated<Fund>
  ): void;

  [MT.SET_ALL_ASSET_CLASSES](
    state: ICostAnalysisState,
    allAssetClasses: OrganisationType[]
  ): void;

  [MT.SET_ALL_TIME_PERIODS](
    state: ICostAnalysisState,
    allTimePeriods: TimePeriod[]
  ): void;

  [MT.SET_ALL_ORGANISATION_TYPES](
    state: ICostAnalysisState,
    allOrganisationTypes: OrganisationType[]
  ): void;

  [MT.SET_ALL_ASSET_MANAGERS](
    state: ICostAnalysisState,
    allAssetManagers: any[]
  ): void;

  [MT.SET_ALL_FUNDS_PER_ORGANISATION](
    state: ICostAnalysisState,
    allFunds: AnalysisFunds[]
  ): void;

  [MT.SET_ALL_EXPOSED_FUNDS_PER_ORGANISATION](
    state: ICostAnalysisState,
    allExposedFunds: AnalysisFunds[]
  ): void;

  [MT.SET_ALL_EXPOSED_PENSION_SCHEMES](
    state: ICostAnalysisState,
    allExposedPensionSchemes: AnalysisPensionScheme[]
  ): void;

  [MT.SET_CURRENT_ORGANISATION](
    state: ICostAnalysisState,
    currentOrganisation: Organisation
  ): void;

  [MT.SET_APPLIED_FILTERS](
    state: ICostAnalysisState,
    appliedFilters: AppliedAnalysisFilter
  ): void;

  [MT.SET_FILTER_FUNDS](
    state: ICostAnalysisState,
    filterFunds: AnalysisFunds[]
  ): void;

  [MT.SET_FILTER_PENSION_SCHEMES](
    state: ICostAnalysisState,
    filterPensionSchemes: AnalysisPensionScheme[]
  ): void;

  [MT.SET_FILTER_ASSET_CLASSES](
    state: ICostAnalysisState,
    filterAssetClasses: OrganisationType[]
  ): void;

  [MT.SET_FILTER_TIME_PERIODS](
    state: ICostAnalysisState,
    filterTimePeriods: TimePeriod[]
  ): void;
}
